import {
    ChangeDetectionStrategy,
    Component,
    effect,
    SecurityContext,
    type ElementRef,
    computed,
    inject,
    input,
    output,
    signal,
    viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { DomSanitizer } from '@angular/platform-browser';
import { NgIcon } from '@ng-icons/core';
import { injectUser } from '@quipex/core/auth';
import { GENERAL_ERROR, injectError } from '@quipex/core/http';
import { qpxToast as toast } from '@quipex/core/utils';
import {
    QpxAvatar,
    QpxButton,
    QpxDateTimePipes,
    QpxIcon,
    QpxTypography,
} from '@quipex/shared/ui';
import { marked } from '@quipex/shared/utils/markdown';
import { interval, map } from 'rxjs';
import type {
    QuippyAssistantAppearance,
    QuippyDocumentOption,
    QuippyMessage,
} from '../../../runtime';
import { QuippyPackAction } from '../pack-action/pack-action';
import { QuippyRelatedDocuments } from '../related-documents';
import { QuippyTypingIndicator } from '../typing-indicator';
import { QuippyMessageDocumentOptions } from './document-options/document-options';
import { QuippyMessageEditPanel } from './edit-panel';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'article[qpx-quippy-message-item]',
    imports: [
        NgIcon,
        QpxAvatar,
        QpxButton,
        MatButtonModule,
        QpxDateTimePipes,
        QpxIcon,
        QpxTypography,
        QuippyMessageEditPanel,
        QuippyMessageDocumentOptions,
        QuippyTypingIndicator,
        QuippyRelatedDocuments,
        QuippyPackAction,
    ],
    templateUrl: './message.html',
    styleUrls: ['./message.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'message-container',
        '[class.current-user]': 'isFromCurrentUser()',
        '[class.assistant]': '!isFromCurrentUser()',
        '[class.pending]': 'isPending()',
        '[class.error]': 'isError()',
        '[attr.aria-label]': 'ariaLabel()',
    },
})
export class QuippyMessageItem {
    //#region Dependency injection
    private readonly sanitizer = inject(DomSanitizer);
    private readonly me = injectUser();
    private readonly clientError = injectError();
    //#endregion

    //#region Inputs
    public readonly message = input.required<QuippyMessage>();
    public readonly appearance = input<QuippyAssistantAppearance>('elevated');
    public readonly isThreadLoading = input.required<boolean>();
    public readonly sourceFallbackBuildingId = input<string | null>(null);
    //#endregion

    //#region Outputs
    public readonly retry = output<QuippyMessage>();
    public readonly editResend = output<{
        message: QuippyMessage;
        content: string;
    }>();
    public readonly documentOptionSelected = output<{
        message: QuippyMessage;
        option: QuippyDocumentOption;
    }>();
    //#endregion

    //#region State
    protected readonly isFromCurrentUser = computed(
        () => this.message().role === 'user'
    );
    protected readonly isPending = computed(
        () => this.message().status === 'pending'
    );
    protected readonly isError = computed(
        () => this.message().status === 'error'
    );
    protected readonly now = toSignal(
        interval(15 * 60 * 1000).pipe(map(() => new Date())),
        { initialValue: new Date() }
    );
    protected readonly ariaLabel = computed(() =>
        this.isFromCurrentUser() ? 'Your message' : 'Quippy response'
    );
    protected readonly userInitials = computed(() => this.me.initials() || 'U');
    protected readonly sanitizedContent = computed(() =>
        this.formatText(this.message().content)
    );
    protected readonly hasDocumentOptions = computed(
        () => (this.message().documentOptions?.length ?? 0) > 0
    );
    protected readonly showActions = computed(
        () => this.message().kind === 'message' && !this.isPending()
    );
    protected readonly isEditing = signal(false);
    protected readonly editDraft = signal('');
    protected readonly canResendEdit = computed(
        () => !!this.editDraft().trim() && !this.isThreadLoading()
    );
    private readonly shouldRestoreEditButtonFocus = signal(false);
    protected readonly messageContextLabel = computed(() =>
        this.buildMessageContextLabel(this.message())
    );
    protected readonly editActionLabel = computed(
        () => `Edit message ${this.messageContextLabel()}`
    );
    protected readonly retryActionLabel = computed(
        () => `Retry message ${this.messageContextLabel()}`
    );
    protected readonly copyActionLabel = computed(
        () => `Copy message ${this.messageContextLabel()}`
    );
    protected readonly editFieldLabel = computed(
        () => `Edit message ${this.messageContextLabel()}`
    );
    //#endregion

    //#region Template references
    protected readonly editButton =
        viewChild<ElementRef<HTMLButtonElement>>('editButton');
    //#endregion

    //#region Lifecycle hooks
    constructor() {
        effect(() => {
            if (this.isEditing() || !this.shouldRestoreEditButtonFocus())
                return;

            queueMicrotask(() => {
                this.editButton()?.nativeElement?.focus();
                this.shouldRestoreEditButtonFocus.set(false);
            });
        });
    }
    //#endregion

    //#region Template methods
    protected onStartEdit(): void {
        if (!this.isFromCurrentUser() || this.isThreadLoading()) return;

        this.editDraft.set(this.message().content);
        this.isEditing.set(true);
    }

    protected onEditInput(value: string): void {
        this.editDraft.set(value);
    }

    protected onCancelEdit(): void {
        this.isEditing.set(false);
        this.editDraft.set('');
        this.shouldRestoreEditButtonFocus.set(true);
    }

    protected onResendEdit(): void {
        const content = this.editDraft().trim();
        if (!content || this.isThreadLoading()) {
            return;
        }

        this.editResend.emit({ message: this.message(), content });
        this.isEditing.set(false);
    }

    protected onRetry(): void {
        if (this.isThreadLoading()) return;

        this.retry.emit(this.message());
    }

    protected onCopy(): void {
        toast.task(
            async () => {
                try {
                    const clipboard = globalThis.navigator?.clipboard;
                    if (!clipboard) throw new Error('Clipboard unavailable');
                    await clipboard.writeText(this.message().content);
                } catch {
                    throw this.clientError.create(
                        GENERAL_ERROR.ClipboardWriteFailed,
                        {
                            urnParts: [
                                'component',
                                'quippy-message',
                                'clipboard-write',
                            ],
                        }
                    );
                }
            },
            { success: 'Message copied', error: {} }
        );
    }

    protected onDocumentOptionSelected(option: QuippyDocumentOption): void {
        const message = this.message();
        if (message.documentSelectionStatus === 'pending') return;
        this.documentOptionSelected.emit({ message, option });
    }

    //#endregion

    //#region Private methods
    private formatText(input: string): string {
        if (!input) return '';
        const htmlContent = marked.parse(input) as string;
        const sanitizedContent = this.sanitizer.sanitize(
            SecurityContext.HTML,
            htmlContent
        );
        return sanitizedContent ?? '';
    }

    private buildMessageContextLabel(message: QuippyMessage): string {
        const snippet = message.content.replace(/\s+/g, ' ').trim();
        const shortSnippet =
            snippet.length > 80 ? `${snippet.slice(0, 77)}...` : snippet;
        const fallbackSnippet = shortSnippet || 'empty message';
        return `sent at ${message.timestamp.toLocaleString()}: ${fallbackSnippet}`;
    }
    //#endregion
}
